import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap, tap } from 'rxjs';
import { MaintenanceApi } from '../../api/maintenance.api';
import {
  LocalStorageKeys,
  LocalStorageService,
} from '../../services/local-storage.service';
import { withRequestStatus } from '../features/request-status.feature';

type MaintenanceState = {
  isUnderMaintenance: boolean;
  // Per-browser override: when on, the client sends the `skip-maintenance`
  // header so an admin can use the app while it is under maintenance for
  // everyone else. Persisted in localStorage so it survives reloads.
  skipMaintenance: boolean;
};

const initialState: MaintenanceState = {
  isUnderMaintenance: false,
  skipMaintenance:
    LocalStorageService.get(LocalStorageKeys.SKIP_MAINTENANCE) === 'true',
};

export const MaintenanceStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus(['getStatus', 'setMaintenance']),
  withMethods((store, maintenanceApi = inject(MaintenanceApi)) => ({
    setUnderMaintenance(isUnderMaintenance: boolean): void {
      patchState(store, { isUnderMaintenance });
    },
    setSkipMaintenance(skipMaintenance: boolean): void {
      if (skipMaintenance) {
        LocalStorageService.set(LocalStorageKeys.SKIP_MAINTENANCE, 'true');
      } else {
        LocalStorageService.remove(LocalStorageKeys.SKIP_MAINTENANCE);
      }
      patchState(store, { skipMaintenance });
    },
    getStatus: rxMethod<void>(
      switchMap(() =>
        maintenanceApi.getStatus().pipe(
          store.handleActionPipe('getStatus', undefined),
          tap((status) =>
            patchState(store, {
              isUnderMaintenance: status.isUnderMaintenance,
            }),
          ),
        ),
      ),
    ),
    setMaintenance: rxMethod<boolean>(
      switchMap((enabled) =>
        maintenanceApi.setMaintenance(enabled).pipe(
          store.handleActionPipe('setMaintenance', undefined),
          tap((status) => {
            patchState(store, {
              isUnderMaintenance: status.isUnderMaintenance,
            });
            // Disabling maintenance also clears the per-browser skip override,
            // so a later maintenance window is not silently bypassed.
            if (!status.isUnderMaintenance) {
              LocalStorageService.remove(LocalStorageKeys.SKIP_MAINTENANCE);
              patchState(store, { skipMaintenance: false });
            }
          }),
        ),
      ),
    ),
  })),
);
